import { useEffect, useState } from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {getStatusChip, isComplete, STATUS_MODEL_CARD} from '../../External/Model/Model';
import { useDispatch, useSelector } from 'react-redux';
import {
    GetInvitationById, publicGenerateICSByInvitation, PublicGetQrCodeByUserId
} from '../../../../../reduxToolkit/Invitation/InvitationApi';
import { useTranslation } from 'react-i18next';
import {status, STATUS_MODEL} from '../../../../../enums/statusEnum';
import { UpdatePrivacyConsent } from "../../../../../reduxToolkit/Visitor/VisitorApi";
import {downloadICalendarFile, errorMsg, getFullName, tryGetAddress} from "../../../../../Apis/functions";
import { toast } from "react-toastify";
import {chipStatusClassColor} from '../../../../../enums/statusColorEnum';
import {GetUserRestriction} from "../../../../../reduxToolkit/restrictions/RestrictionApi";
import { ACTION_MODEL } from '../../../../../Helpers/ActionModel';
import QRCode from "qrcode";
/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 */
const InvitationApp = () => {
    const navigate = useNavigate();
    const { id, guardFlowId } = useParams();
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const { getInvitationById, updateVisitorStatus, publicGetQrCodeByUserId } = useSelector((state) => state.InvitationSlice);
    const { getUserRestriction }  = useSelector((state) => state.RestrictionSlice);

    const [visitorStatus, setVisitorStatus] = useState({ ...STATUS_MODEL_CARD.UNATTENDED }); // visitor_status_id
    const [visitorStatusBadge, setVisitorStatusBadge] = useState(null);
    const [showBadgeMessage, setBadgeMessage] = useState(true);

    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);
    const [monthDay, setMonthDay] = useState(null);
    const [latLng, setLatLng] = useState([]);
    const [privacyConsentDate, setPrivacyConsentDate] = useState(null);
    const [chipStatusInvitation, setChipStatusInvitation] = useState(null);
    const [direction, setDirection] = useState(null);
    /**
     * Gets the invitation data.
     */
    useEffect(() => {
        if(id){
            dispatch(GetUserRestriction());
            dispatch(GetInvitationById(id)).then(({ payload: { data: {data, success }}}) => {
                if(success) dispatch(PublicGetQrCodeByUserId(data?.invitation?.visitorId))
            });
        }
    }, [updateVisitorStatus, id]);


    useEffect(() => {
        if(getInvitationById?.invitation?.statusId == STATUS_MODEL.CANCELED){
            setChipStatusInvitation({name: "canceled_by_admin", className: "chip-error-code-150-100"});
        }else {
            setChipStatusInvitation({name: status[getInvitationById?.invitation?.visitorStatusId], className: chipStatusClassColor[getInvitationById?.invitation?.visitorStatusId] });
        }
    }, [getInvitationById]);

    /**
     * Control of badge and follow-up card assignments according to the defined variables.
     * Assignment of the month and day label, assignment of latitude and longitude to a useState.
     */

    useEffect(() => {
        if (getInvitationById?.invitation?.id && getUserRestriction) {
            const invitationId = getInvitationById?.invitation?.id;
            const identityValidation = getInvitationById?.invitation?.identityValidation; // control of: id card, passport, face
            const registeredFiles = getInvitationById?.registeredFiles; // registeredFiles: id card, passport
            const statusId = getInvitationById?.invitation?.statusId; // files status control and canceled by employee
            const visitorStatusId = getInvitationById?.invitation?.visitorStatusId;  // invitation status control
            const startDate = getInvitationById?.invitation?.startDate; // invitation start date

            setMonthDay({ month: getMonth(getInvitationById?.invitation?.startDate), day: getDay(getInvitationById?.invitation?.startDate) });
            setLatitude(getInvitationById?.invitation?.latitud)
            setLongitude(getInvitationById?.invitation?.longitud)
            setPrivacyConsentDate(getInvitationById?.invitation?.privacyConsentDate)

            const STATUS_CHIP = getStatusChip(invitationId, guardFlowId);

            if(parseInt(statusId) === STATUS_MODEL.CANCELED){
                setVisitorStatus({ ...STATUS_MODEL_CARD.CANCELED_BY_EMPLOYEE});
                return;
            }

            if(guardFlowId){
                guardFlowIdValidations(visitorStatusId, statusId, identityValidation, registeredFiles, startDate, STATUS_CHIP);
                return;
            }

            validateVisitorStatusAndBadge(visitorStatusId, statusId, identityValidation, registeredFiles, startDate, STATUS_CHIP);
        }
    }, [getInvitationById, getUserRestriction]);

    /**
     * Validates the guard flow for visitor registration and updates the visitor status
     * based on privacy consent, current status, identity validation, registered files,
     * and invitation start date.
     */
    const guardFlowIdValidations = (visitorStatusId, statusId, identityValidation, registeredFiles, startDate, STATUS_CHIP) => {
        if(getInvitationById?.invitation?.privacyConsentDate) {
            if (visitorStatusId === STATUS_MODEL.ACCEPTED){
                if((isComplete(getUserRestriction,statusId, identityValidation))){
                    validateStatusAccepted(statusId, identityValidation, registeredFiles, startDate, STATUS_CHIP);
                    return;
                }

                setVisitorStatus({...STATUS_MODEL_CARD.PROCEED_WITH_REGISTRATION});
            }else{
                setVisitorStatus({...STATUS_MODEL_CARD.VISITOR_CONFIRMATION_REQUIRED});
            }
        }else{
            setVisitorStatus({ ...STATUS_MODEL_CARD.BEFORE_STARTING});
        }
    }

    /**
     * Invitation control, if it is in UNATTENDED, REJECTED, CANCELED, or ACCEPTED status according to the visitorStatusId variable.
     */
    const validateVisitorStatusAndBadge = (visitorStatusId, statusId, identityValidation, registeredFiles, startDate, STATUS_CHIP) => {
        switch (visitorStatusId) {
            case STATUS_MODEL.UNATTENDED:
                setVisitorStatus({ ...STATUS_MODEL_CARD.UNATTENDED });
                break;
            case STATUS_MODEL.REJECTED:
                setVisitorStatus({ ...STATUS_MODEL_CARD.REJECTED });
                break;
            case STATUS_MODEL.CANCELED:
                setVisitorStatus({ ...STATUS_MODEL_CARD.CANCELED });
                break;
            case STATUS_MODEL.ACCEPTED:
                validateStatusAccepted(statusId, identityValidation, registeredFiles, startDate, STATUS_CHIP);
                break;
            default:
                break;
        }
    }

    /**
     * Assignment status control for the VisitorStatusBadge and VisitorStatus variables; this is validated using statusId and identityValidation.
     */
    const validateStatusAccepted = (statusId, identityValidation, registeredFiles, startDate, STATUS_CHIP) => {
        if (parseInt(identityValidation) === STATUS_MODEL.VALIDATED && parseInt(statusId) === STATUS_MODEL.APPROVED) {
            setVisitorStatusBadge({ ...STATUS_CHIP.ALL_SET });
            setVisitorStatus({ ...STATUS_MODEL_CARD.VALIDATED });
            return;
        }
        if (parseInt(statusId) === STATUS_MODEL.CHANGES_REQUESTED) {
            setVisitorStatusBadge({ ...STATUS_CHIP.UPDATE })
        }

        if (parseInt(identityValidation) === STATUS_MODEL.PENDING_REVIEW && parseInt(statusId) === STATUS_MODEL.PENDING_REVIEW) {
            setVisitorStatus({ ...STATUS_MODEL_CARD.PENDING_REVIEW });
            return;
        }
        handleStartOrContinue(registeredFiles,STATUS_CHIP ,startDate);
    }


    /**
     * Function to display the "Start validation process" or "Continue validation process" button.
     */
    const handleStartOrContinue = (registeredFiles,STATUS_CHIP ,startDate) => {
        if (registeredFiles == 0) {
            setVisitorStatusBadge({ ...STATUS_CHIP.COMPLETE });
            setVisitorStatus({ ...STATUS_MODEL_CARD.ACCEPTED });
        } else {
            setVisitorStatus({ ...STATUS_MODEL_CARD.REGISTRATION_IN_PROGRESS });
            if (visitIsComingUp(startDate))
                setVisitorStatusBadge({ ...STATUS_CHIP.REMINDER })
        }
    }

    /**
     * Function to check if the invitation is close.
     */
    const visitIsComingUp = (timestampInMs) => {
        const now = new Date();
        const target = new Date(timestampInMs);

        const isSameDay =
            now.getFullYear() === target.getFullYear() &&
            now.getMonth() === target.getMonth() &&
            now.getDate() === target.getDate();

        return isSameDay && timestampInMs > Date.now();
    };


    /**
     * Function to assign the location obtained from the getLocation function to the variables latitude and longitude.
     */
    useEffect(() => {
        if (latitude && longitude) {
            getLocationData(latitude, longitude);
            setLatLng([latitude, longitude])
        }
    }, [latitude, longitude]);

    const getLocationData = (lat, lng) => {
        setDirection(t("loading"))
        tryGetAddress(lat, lng, dispatch, setDirection);
    }

    /**
     * event to close badge
     */
    const handleCloseBadge = () => {
        setBadgeMessage(false);
    }

    /**
     * Function to get the short month.
     */
    const getMonth = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleString('es-ES', { month: 'short' });
    }

    /**
     * Function to get the day from a date.
     * @param timestamp
     * @returns {number}
     */
    const getDay = (timestamp) => {
        const date = new Date(timestamp);
        return date.getDate();
    }

    const handleUpdatePrivacyConsent = (e) => {
        let checked = e.target.checked;

        dispatch(UpdatePrivacyConsent(getInvitationById?.invitation?.visitorId)).then(({ payload: { data: { success, code } } }) => {
            if (success) {
                toast.success(t("has_been_accepted_successfully"))
                setPrivacyConsentDate(checked);
            } else errorMsg(code);
        });
    }

    const handleAddRemoveToCalendar = () => {
        let isCancelled = parseInt(getInvitationById?.invitation?.visitorStatusId) === STATUS_MODEL.CANCELED;
        dispatch(publicGenerateICSByInvitation({
            invitationId: id,
            action: isCancelled ? ACTION_MODEL.DELETE: ACTION_MODEL.CREATE
        })).then(({payload: {data }}) => {
            let hostName = getFullName(getInvitationById?.invitation?.hostName, getInvitationById?.invitation?.hostLastName, getInvitationById?.invitation?.hostSecondLastName);
            let name = `${t(isCancelled ? "cancellation_of_invitation" : "invitation_by")} ${hostName}`;
            if (data) downloadICalendarFile(data, name);
        });
    }

    /**
     * Downloads a QR code as a PNG file.
     *
     * The function serializes the `publicGetQrCodeByUserId` object,
     * generates a QR code image (300px width), creates a temporary
     * `<a>` element, and triggers a download using the translated
     * filename for "qr_code".
     *
     * If QR generation fails, an error toast is shown.
     *
     * @async
     * @function handleDownload
     */
    const handleDownload = async () => {
        if(!publicGetQrCodeByUserId) return;
        try {
            const image = await QRCode.toDataURL(String(publicGetQrCodeByUserId), { width: 300 });
            const link = document.createElement('a');
            link.href = image;
            link.download = t("qr_code") + '.png';
            link.click();
        } catch (err) {
            toast.error(t('something_went_wrong_2 '));
        }
    };

    return {
        t,
        navigate,
        guardFlowId,
        getInvitationById,
        visitorStatus, setVisitorStatus,
        visitorStatusBadge, setVisitorStatusBadge,
        showBadgeMessage, setBadgeMessage,
        handleCloseBadge,
        latitude,
        setLatitude,
        longitude,
        setLongitude,
        monthDay,
        latLng,
        direction,
        privacyConsentDate,
        handleUpdatePrivacyConsent,
        chipStatusInvitation,
        getUserRestriction,
        handleAddRemoveToCalendar,
        publicGetQrCodeByUserId,
        handleDownload
    }
}

export default InvitationApp
